import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Modal } from "@tyrannosaurustech/ui";
import { Theme } from "../../../components/atoms/theme";
import { useGetRequestSilentQuery } from "../../../redux/reduxQuery/requestApi";
import { useGetProviderSilentQuery } from "../../../redux/reduxQuery/medicalProviderApi";
import { useNavigate } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  Constants & tiny utilities                                        */
/* ------------------------------------------------------------------ */

const EXCLUDED_FIELDS = [
  "id",
  "createdAt",
  "updatedAt",
  "SearchField",
  "searchField",
];

const UUID_REGEX =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const formatFieldName = (field: string): string =>
  field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/\bId\b/g, "ID")
    .trim();

const isISODateString = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isISODateString(value)) {
    return new Date(value as string).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }
  return String(value);
};

// Detects list-like values (emails separated by `;`, comma lists, etc.)
const splitListValue = (value: unknown): string[] | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/[;,]\s*/.test(trimmed)) {
    const parts = trimmed
      .split(/[;,]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length > 1) return parts;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/*  Design tokens (scoped to this modal only)                         */
/* ------------------------------------------------------------------ */

const C = {
  brand: Theme.colors.primaries_000,
  brandDark: Theme.colors.primaries_neg100,
  brandMid: Theme.colors.primaries_100,
  brandLight: Theme.colors.primaries_300,
  brandTint: "#FBF5FD", // very subtle lavender wash — matches app brand
  // App-native neutral palette (slate) — matches the rest of RAYNA
  ink: "#0F172A",
  body: "#334155",
  muted: "#64748B",
  subtle: "#94A3B8",
  hair: "#E2E8F0",
  hairStrong: "#CBD5E1",
  card: "#FFFFFF",
  canvas: "#FFFFFF",
  canvasSoft: "#F8FAFC",
  canvasDeep: "#F1F5F9",
  // Web-standard semantic colors, restrained intensity
  removedBg: "#FEF2F2",
  removedInk: "#B91C1C",
  removedBorder: "#FECACA",
  addedBg: "#F0FDF4",
  addedInk: "#15803D",
  addedBorder: "#BBF7D0",
  warnBg: "#FFFBEB",
  warnInk: "#B45309",
  warnBorder: "#FDE68A",
};

const MONO = `ui-monospace, "SF Mono", "Cascadia Mono", Menlo, Consolas, monospace`;

/* ------------------------------------------------------------------ */
/*  Styled primitives                                                 */
/* ------------------------------------------------------------------ */

/* Entry animations — staggered subtle reveal */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Root = styled.div`
  margin: 0;
  padding: 0;
  color: ${C.ink};
  background: ${C.canvas};
  position: relative;
  font-feature-settings: "ss01", "ss02", "cv11";

  /* Top accent rail — single thin brand-colored line as the signature detail */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${C.brand} 0%,
      ${C.brandMid} 40%,
      ${C.brandLight} 100%
    );
    z-index: 2;
  }
`;

const Hero = styled.header`
  position: relative;
  padding: 1.75rem 2rem 1.5rem;
  border-bottom: 1px solid ${C.hair};
  background: linear-gradient(180deg, ${C.brandTint} 0%, ${C.canvas} 85%);
  /* Extremely subtle paper-grain dot grid for a crafted feel */
  background-image: radial-gradient(
      circle at 1px 1px,
      rgba(134, 46, 156, 0.04) 1px,
      transparent 0
    ),
    linear-gradient(180deg, ${C.brandTint} 0%, ${C.canvas} 85%);
  background-size: 18px 18px, auto;
  animation: ${fadeInUp} 280ms ease both;
`;

const HeroTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.125rem;
`;

const KickerRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  font-family: ${MONO};
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.muted};
`;

const EventDot = styled.span<{
  tone?: "brand" | "success" | "danger" | "warn";
}>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex: 0 0 auto;
  background: ${({ tone }) =>
    tone === "success"
      ? C.addedInk
      : tone === "danger"
      ? C.removedInk
      : tone === "warn"
      ? C.warnInk
      : C.brand};
  box-shadow: 0 0 0 4px
    ${({ tone }) =>
      tone === "success"
        ? C.addedBg
        : tone === "danger"
        ? C.removedBg
        : tone === "warn"
        ? C.warnBg
        : C.brandLight};
`;

const KickerSep = styled.span`
  color: ${C.subtle};
  opacity: 0.7;
  user-select: none;
`;

const EntryId = styled.span`
  font-family: ${MONO};
  font-size: 0.6875rem;
  color: ${C.subtle};
  letter-spacing: 0.06em;
  padding: 0.125rem 0.5rem;
  border: 1px solid ${C.hair};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
`;

const BigTitle = styled.h1`
  margin: 0 0 1rem;
  font-size: 1.625rem;
  line-height: 1.18;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: ${C.ink};
  max-width: 34ch;
  /* Typographic emphasis — brand-colored field names inside a span */
  em {
    font-style: normal;
    color: ${C.brandDark};
    background: linear-gradient(
      180deg,
      transparent 62%,
      ${C.brandLight} 62%,
      ${C.brandLight} 92%,
      transparent 92%
    );
    padding: 0 0.125rem;
  }
`;

const ActorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  color: ${C.body};
`;

const Initial = styled.span`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: ${C.brandLight};
  color: ${C.brandDark};
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex: 0 0 auto;
`;

const ActorName = styled.span`
  font-weight: 600;
  color: ${C.ink};
`;

const ActorMeta = styled.span`
  color: ${C.muted};
  font-family: ${MONO};
  font-size: 0.75rem;
  letter-spacing: 0.04em;
`;

const Footer = styled.div`
  padding: 1rem 1.5rem 1.125rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  background: ${C.canvas};
  border-top: 1px solid ${C.hair};
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: 0;
  padding: 0.5rem 1.125rem;
  border-radius: 8px;
  background: ${C.brand};
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;
  box-shadow: 0 1px 2px rgba(174, 62, 201, 0.3), 0 0 0 1px ${C.brandDark} inset;
  &:hover {
    background: ${C.brandDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(174, 62, 201, 0.35),
      0 0 0 1px ${C.brandDark} inset;
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid ${C.brandMid};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  padding: 1.5rem 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  max-height: 58vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${C.canvas};
  /* Custom scrollbar for Webkit */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.hairStrong};
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${C.subtle};
  }

  /* Stagger children fade-in */
  & > *:nth-child(1) {
    animation: ${fadeInUp} 320ms 40ms ease both;
  }
  & > *:nth-child(2) {
    animation: ${fadeInUp} 320ms 90ms ease both;
  }
  & > *:nth-child(3) {
    animation: ${fadeInUp} 320ms 140ms ease both;
  }
  & > *:nth-child(4) {
    animation: ${fadeInUp} 320ms 190ms ease both;
  }
  & > *:nth-child(n + 5) {
    animation: ${fadeInUp} 320ms 240ms ease both;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SectionLabel = styled.p`
  margin: 0;
  font-family: ${MONO};
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.muted};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  /* Decorative rule — extends from label to the right */
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${C.hair};
    margin-left: 0.25rem;
  }
`;

const Sentence = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.55rem;
  color: ${C.body};
  letter-spacing: -0.005em;
  strong {
    font-weight: 600;
    color: ${C.ink};
  }
`;

const DiffCard = styled.article`
  border: 1px solid ${C.hair};
  border-radius: 12px;
  background: ${C.card};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(11, 11, 15, 0.02);
  transition: transform 200ms ease, box-shadow 200ms ease,
    border-color 200ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(11, 11, 15, 0.06);
    border-color: ${C.hairStrong};
  }
`;

const DiffHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  background: ${C.canvasSoft};
  border-bottom: 1px solid ${C.hair};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${C.ink};
  letter-spacing: -0.005em;
`;

const DiffBody = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: 0;
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const DiffSide = styled.div<{ tone: "old" | "new" }>`
  padding: 1.125rem 1.25rem;
  background: ${({ tone }) => (tone === "old" ? C.removedBg : C.addedBg)};
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-width: 0;
`;

const DiffSideLabel = styled.div<{ tone: "old" | "new" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-family: ${MONO};
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ tone }) => (tone === "old" ? C.removedInk : C.addedInk)};
  opacity: 0.85;
`;

const DiffValue = styled.div<{ tone: "old" | "new"; strike?: boolean }>`
  font-size: 0.9375rem;
  line-height: 1.45rem;
  color: ${({ tone }) => (tone === "old" ? C.removedInk : C.addedInk)};
  ${({ strike, tone }) =>
    strike && tone === "old"
      ? `text-decoration: line-through; text-decoration-thickness: 1px; text-decoration-color: ${C.removedInk}; opacity: 0.85;`
      : ""}
  word-break: break-word;
  font-weight: 500;
  letter-spacing: -0.005em;
`;

const Arrow = styled.div`
  display: grid;
  place-items: center;
  padding: 0 0.5rem;
  background: ${C.card};
  border-left: 1px solid ${C.hair};
  border-right: 1px solid ${C.hair};
  color: ${C.brand};
  position: relative;

  /* Subtle pulsing dot behind the arrow for visual anchor */
  &::before {
    content: "";
    position: absolute;
    inset: 50% 50% auto auto;
    transform: translate(50%, -50%);
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: ${C.brandLight};
    opacity: 0.6;
    z-index: 0;
  }
  svg {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 540px) {
    border-left: none;
    border-right: none;
    border-top: 1px solid ${C.hair};
    border-bottom: 1px solid ${C.hair};
    padding: 0.375rem 0;
    transform: rotate(90deg);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3125rem;
`;

const Chip = styled.span<{ tone: "old" | "new" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  border-radius: 999px;
  background: ${({ tone }) =>
    tone === "old"
      ? "rgba(185, 28, 28, 0.08)"
      : tone === "new"
      ? "rgba(21, 128, 61, 0.1)"
      : "rgba(100, 116, 139, 0.1)"};
  color: ${({ tone }) =>
    tone === "old" ? C.removedInk : tone === "new" ? C.addedInk : C.body};
  border: 1px solid
    ${({ tone }) =>
      tone === "old"
        ? C.removedBorder
        : tone === "new"
        ? C.addedBorder
        : C.hair};
`;

const EntityCard = styled.div`
  border: 1px solid ${C.hair};
  border-radius: 0.75rem;
  background: ${C.card};
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const DefRow = styled.div`
  display: grid;
  grid-template-columns: 9.5rem 1fr;
  gap: 1rem;
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  &:nth-child(odd) {
    background: ${C.canvas};
  }
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
`;

const DefLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${C.muted};
  text-transform: capitalize;
`;

const DefValue = styled.div`
  font-size: 0.875rem;
  color: ${C.ink};
  word-break: break-word;
`;

const LinkPill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.3125rem 0.75rem 0.3125rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${C.brandDark};
  background: ${C.card};
  border: 1px solid ${C.brandLight};
  border-radius: 999px;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease,
    border-color 160ms ease, background 160ms ease;
  cursor: pointer;
  letter-spacing: -0.005em;
  position: relative;

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 999px;
    background: ${C.brand};
    flex: 0 0 auto;
    box-shadow: 0 0 0 3px ${C.brandLight};
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(174, 62, 201, 0.16);
    border-color: ${C.brandMid};
    background: ${C.brandLight};
  }
`;

const StatusPill = styled.span<{ tone: "old" | "new" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${({ tone }) => (tone === "old" ? C.removedBg : C.addedBg)};
  color: ${({ tone }) => (tone === "old" ? C.removedInk : C.addedInk)};
  border: 1px solid
    ${({ tone }) => (tone === "old" ? C.removedBorder : C.addedBorder)};
`;

const StatusFlow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.125rem 1.25rem;
  border-radius: 0.75rem;
  background: ${C.card};
  border: 1px solid ${C.hair};
  flex-wrap: wrap;
`;

const Stat = styled.div<{ tone: "success" | "danger" | "neutral" }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: ${({ tone }) =>
    tone === "success"
      ? C.addedBg
      : tone === "danger"
      ? C.removedBg
      : C.canvas};
  border: 1px solid
    ${({ tone }) =>
      tone === "success"
        ? C.addedBorder
        : tone === "danger"
        ? C.removedBorder
        : C.hair};
`;

const StatNumber = styled.div<{ tone: "success" | "danger" | "neutral" }>`
  font-size: 1.5rem;
  line-height: 1.5rem;
  font-weight: 700;
  color: ${({ tone }) =>
    tone === "success" ? C.addedInk : tone === "danger" ? C.removedInk : C.ink};
  letter-spacing: -0.01em;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: ${C.body};
  font-weight: 500;
`;

const SoftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  border: 1px solid ${C.hair};
  border-radius: 0.75rem;
  background: ${C.card};
  padding: 0.5rem;
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  background: ${C.canvas};
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: ${C.body};
`;

const EmptyNote = styled.p`
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${C.muted};
  font-style: italic;
  background: ${C.canvas};
  border: 1px dashed ${C.hair};
  border-radius: 0.5rem;
`;

/* ------------------------------------------------------------------ */
/*  Inline icons (no extra deps)                                      */
/* ------------------------------------------------------------------ */

const Svg = ({
  children,
  size = 20,
}: {
  children: React.ReactNode;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const icons = {
  edit: (
    <Svg>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </Svg>
  ),
  plus: (
    <Svg>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  trash: (
    <Svg>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </Svg>
  ),
  swap: (
    <Svg>
      <path d="M7 7h14l-4-4M17 17H3l4 4" />
    </Svg>
  ),
  layers: (
    <Svg>
      <path d="m12 2 10 6-10 6L2 8Zm0 8 10 6-10 6L2 16" />
    </Svg>
  ),
  check: (
    <Svg size={14}>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  ),
  x: (
    <Svg size={14}>
      <path d="M18 6 6 18M6 6l12 12" />
    </Svg>
  ),
  arrow: (
    <Svg size={18}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </Svg>
  ),
  link: (
    <Svg size={14}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Svg>
  ),
  paperclip: (
    <Svg>
      <path d="M21 10.5 12.5 19a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10.5 17a2 2 0 0 1-2.8-2.8l7-7" />
    </Svg>
  ),
  user: (
    <Svg size={14}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Svg>
  ),
};

/* ------------------------------------------------------------------ */
/*  Live reference lookups                                            */
/* ------------------------------------------------------------------ */

const RequestReference = ({ requestId }: { requestId: string }) => {
  const navigate = useNavigate();
  const { data: request, isLoading } = useGetRequestSilentQuery(requestId, {
    skip: !requestId,
  }) as { data: any; isLoading: boolean };

  const label = useMemo(() => {
    if (isLoading) return "Loading request…";
    if (!request) return `Request #${requestId.substring(0, 6)}`;
    const firmName = request?.firm?.name || "Unknown firm";
    const patientName = request?.patient?.name || "Unknown patient";
    return `${firmName} — ${patientName}`;
  }, [request, isLoading, requestId]);

  return (
    <LinkPill
      href={`/admin/requests/${requestId}`}
      onClick={(e) => {
        e.preventDefault();
        navigate(`/admin/requests/${requestId}`);
      }}
    >
      {icons.link}
      {label}
    </LinkPill>
  );
};

const ProviderReference = ({ providerId }: { providerId: string }) => {
  const navigate = useNavigate();
  const { data: provider, isLoading } = useGetProviderSilentQuery(providerId, {
    skip: !providerId,
  }) as { data: any; isLoading: boolean };

  const label = useMemo(() => {
    if (isLoading) return "Loading provider…";
    if (!provider) return `Provider #${providerId.substring(0, 6)}`;
    return provider.name || `Provider #${providerId.substring(0, 6)}`;
  }, [provider, isLoading, providerId]);

  return (
    <LinkPill
      href={`/admin/medical-providers/${providerId}`}
      onClick={(e) => {
        e.preventDefault();
        navigate(`/admin/medical-providers/${providerId}`);
      }}
    >
      {icons.link}
      {label}
    </LinkPill>
  );
};

/* ------------------------------------------------------------------ */
/*  Reusable pieces                                                   */
/* ------------------------------------------------------------------ */

const DiffValueDisplay = ({
  value,
  tone,
}: {
  value: unknown;
  tone: "old" | "new";
}) => {
  const formatted = formatValue(value);
  const list = splitListValue(value);

  if (list) {
    return (
      <ChipRow>
        {list.map((item, i) => (
          <Chip key={`${tone}-${i}`} tone={tone}>
            {tone === "old" ? icons.x : icons.check}
            <span style={{ marginLeft: "0.25rem" }}>{item}</span>
          </Chip>
        ))}
      </ChipRow>
    );
  }

  return (
    <DiffValue tone={tone} strike={tone === "old"}>
      {formatted === "—" ? (
        <span style={{ fontStyle: "italic", opacity: 0.7 }}>
          {tone === "old" ? "nothing" : "empty"}
        </span>
      ) : (
        formatted
      )}
    </DiffValue>
  );
};

const FieldDiff = ({
  field,
  oldValue,
  newValue,
}: {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}) => (
  <DiffCard>
    <DiffHeader>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: C.brand,
          display: "inline-block",
        }}
      />
      {field}
    </DiffHeader>
    <DiffBody>
      <DiffSide tone="old">
        <DiffSideLabel tone="old">
          {icons.x}
          <span>Was</span>
        </DiffSideLabel>
        <DiffValueDisplay value={oldValue} tone="old" />
      </DiffSide>
      <Arrow>{icons.arrow}</Arrow>
      <DiffSide tone="new">
        <DiffSideLabel tone="new">
          {icons.check}
          <span>Now</span>
        </DiffSideLabel>
        <DiffValueDisplay value={newValue} tone="new" />
      </DiffSide>
    </DiffBody>
  </DiffCard>
);

/* ------------------------------------------------------------------ */
/*  Message narrative builder                                         */
/* ------------------------------------------------------------------ */

interface Narrative {
  actor: string | null;
  verb: string;
  friendly: string;
  linkedRequestId: string | null;
  linkedProviderId: string | null;
  eventKind:
    | "create"
    | "update"
    | "delete"
    | "status"
    | "bulk"
    | "attachmentAdd"
    | "attachmentRemove"
    | "generic";
}

const buildNarrative = (
  message: string,
  metadata: Record<string, any> | null,
  hint?: { eventType?: string; userName?: string }
): Narrative => {
  const text = message || "";
  const actorMatch = text.match(
    /^User\s+([^\n]+?)\s+(created|updated|deleted|changed|bulk updated|marked|uploaded|verified|added attachment|removed attachment)/i
  );
  const actor = hint?.userName || (actorMatch ? actorMatch[1] : null);
  const verbRaw = actorMatch ? actorMatch[2].toLowerCase() : "changed";

  // Grab first UUID as the linked entity id
  const uuids = text.match(UUID_REGEX) || [];
  const linkedEntityId = uuids[0] || null;

  // Prefer structured event type if available — it's authoritative
  let eventKind: Narrative["eventKind"] = "generic";
  const et = hint?.eventType;
  if (et === "ATTACHMENT_ADDED") eventKind = "attachmentAdd";
  else if (et === "ATTACHMENT_REMOVED") eventKind = "attachmentRemove";
  else if (et === "CREATE" || et === "MEDICAL_REQUEST_CREATE")
    eventKind = "create";
  else if (et === "UPDATE" || et === "MEDICAL_REQUEST_UPDATE")
    eventKind = "update";
  else if (et === "DELETE" || et === "MEDICAL_REQUEST_DELETE")
    eventKind = "delete";
  else if (et === "STATUS_CHANGE") eventKind = "status";
  else if (et === "BULK_UPDATE") eventKind = "bulk";
  else if (/^added attachment/.test(verbRaw)) eventKind = "attachmentAdd";
  else if (/^removed attachment/.test(verbRaw)) eventKind = "attachmentRemove";
  else if (/^created/.test(verbRaw)) eventKind = "create";
  else if (/^updated/.test(verbRaw)) eventKind = "update";
  else if (/^deleted/.test(verbRaw)) eventKind = "delete";
  else if (/^changed/.test(verbRaw) || metadata?.oldStatus !== undefined)
    eventKind = "status";
  else if (/^bulk/.test(verbRaw) || metadata?.count !== undefined)
    eventKind = "bulk";

  // Strip UUIDs and clean up the message into plain English
  let friendly = text
    .replace(/^User\s+/i, "")
    .replace(UUID_REGEX, "")
    .replace(/\s+/g, " ")
    .replace(/\s+\./g, ".")
    .replace(/\bfor request\b\s*$/i, "")
    .replace(/\bfor request\b/i, "on a request")
    .trim();

  // If message references a request explicitly, tidy it
  friendly = friendly
    .replace(/\brequest\s+$/i, "a request")
    .replace(/\brequest\s+(and|in|\.|,)/i, "a request $1")
    .trim();

  // Detect request id specifically for link rendering
  const isRequest = /request/i.test(text);
  const isProvider = /provider/i.test(text) && !isRequest;
  const linkedRequestId = isRequest ? linkedEntityId : null;
  const linkedProviderId = isProvider ? linkedEntityId : null;

  return {
    actor,
    verb: verbRaw,
    friendly: friendly || text,
    linkedRequestId,
    linkedProviderId,
    eventKind,
  };
};

const friendlyEventTitle = (kind: Narrative["eventKind"]): string => {
  switch (kind) {
    case "create":
      return "Something new was added";
    case "update":
      return "Some details were updated";
    case "delete":
      return "Something was removed";
    case "status":
      return "A status was changed";
    case "bulk":
      return "Multiple items were updated at once";
    case "attachmentAdd":
      return "An attachment was added";
    case "attachmentRemove":
      return "An attachment was removed";
    default:
      return "An activity happened";
  }
};

const iconForKind = (kind: Narrative["eventKind"]) => {
  switch (kind) {
    case "create":
      return { node: icons.plus, tone: "success" as const };
    case "delete":
      return { node: icons.trash, tone: "danger" as const };
    case "status":
      return { node: icons.swap, tone: "warn" as const };
    case "bulk":
      return { node: icons.layers, tone: "brand" as const };
    case "attachmentAdd":
      return { node: icons.paperclip, tone: "success" as const };
    case "attachmentRemove":
      return { node: icons.paperclip, tone: "danger" as const };
    case "update":
    default:
      return { node: icons.edit, tone: "brand" as const };
  }
};

/* ------------------------------------------------------------------ */
/*  The modal                                                         */
/* ------------------------------------------------------------------ */

interface AuditDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: Record<string, any> | null;
  message: string;
  id?: string;
  createdAt?: string;
  eventType?: string;
  entityType?: string;
  userName?: string;
}

/* Build a specific, plain-English headline from metadata when possible.
 * Falls back to generic friendly titles. */
const buildHeadline = (
  narrative: Narrative,
  metadata: Record<string, any> | null
): React.ReactNode => {
  if (!metadata) return friendlyEventTitle(narrative.eventKind);

  // Single-field diffs: call out the field by name
  if (metadata.changes && typeof metadata.changes === "object") {
    const entries = Object.entries(
      metadata.changes as Record<string, { old: any; new: any }>
    ).filter(([field]) => !EXCLUDED_FIELDS.includes(field));

    if (entries.length === 1) {
      const [field, change] = entries[0];
      const label = formatFieldName(field);
      if (typeof change.new === "boolean" && typeof change.old === "boolean") {
        return change.new ? (
          <>
            <em>{label}</em> was turned on.
          </>
        ) : (
          <>
            <em>{label}</em> was turned off.
          </>
        );
      }
      if (
        change.new === null ||
        change.new === undefined ||
        change.new === ""
      ) {
        return (
          <>
            <em>{label}</em> was cleared.
          </>
        );
      }
      if (
        change.old === null ||
        change.old === undefined ||
        change.old === ""
      ) {
        return (
          <>
            <em>{label}</em> was set.
          </>
        );
      }
      return (
        <>
          <em>{label}</em> was updated.
        </>
      );
    }
    if (entries.length > 1) {
      return `${entries.length} fields were updated.`;
    }
  }

  if (metadata.oldStatus !== undefined || metadata.newStatus !== undefined) {
    return (
      <>
        Status changed to <em>{String(metadata.newStatus ?? "—")}</em>.
      </>
    );
  }

  if (metadata.count !== undefined || metadata.succeeded || metadata.failed) {
    const n =
      metadata.count ||
      (Array.isArray(metadata.succeeded) ? metadata.succeeded.length : 0);
    return n === 1 ? `1 item was updated.` : `${n} items were updated at once.`;
  }

  if (metadata.fromId || metadata.toId) {
    return narrative.eventKind === "delete"
      ? "A provider connection was removed."
      : "A provider connection was saved.";
  }

  if (narrative.eventKind === "attachmentAdd") {
    return metadata.fileName ? (
      <>
        <em>{String(metadata.fileName)}</em> was attached.
      </>
    ) : (
      "A file was attached."
    );
  }
  if (narrative.eventKind === "attachmentRemove") {
    return metadata.fileName ? (
      <>
        <em>{String(metadata.fileName)}</em> was removed.
      </>
    ) : (
      "An attachment was removed."
    );
  }

  // Entity creates / deletes
  const entityMap: Array<[string, string]> = [
    ["request", "request"],
    ["medicalRequest", "medical request"],
    ["provider", "provider"],
    ["patient", "patient"],
    ["firm", "firm"],
    ["user", "user"],
    ["patientForm", "patient form"],
  ];
  for (const [key, label] of entityMap) {
    if (
      metadata[key] ||
      metadata[`deleted${key.charAt(0).toUpperCase()}${key.slice(1)}`]
    ) {
      if (narrative.eventKind === "delete") return `A ${label} was removed.`;
      if (narrative.eventKind === "create") return `A new ${label} was added.`;
    }
  }

  if (metadata.created) return "Something new was added.";

  return friendlyEventTitle(narrative.eventKind);
};

const initialsFor = (name?: string | null): string => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("");
};

const relativeTime = (iso?: string): string => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const absoluteTime = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const AuditDetailsModal: React.FC<AuditDetailsModalProps> = ({
  isOpen,
  onClose,
  metadata,
  message,
  id,
  createdAt,
  eventType,
  entityType,
  userName,
}) => {
  const narrative = useMemo(
    () => buildNarrative(message || "", metadata, { eventType, userName }),
    [message, metadata, eventType, userName]
  );

  const heroIcon = iconForKind(narrative.eventKind);
  const headline = useMemo(
    () => buildHeadline(narrative, metadata),
    [narrative, metadata]
  );

  if (!metadata) return null;

  /* -------------------- case renderers ---------------------------- */

  const renderChanges = (changes: Record<string, { old: any; new: any }>) => {
    const filtered = Object.entries(changes).filter(
      ([field]) => !EXCLUDED_FIELDS.includes(field)
    );
    return (
      <Section>
        <SectionLabel>What changed</SectionLabel>
        {filtered.length === 0 ? (
          <EmptyNote>No tracked fields changed.</EmptyNote>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.625rem",
            }}
          >
            {filtered.map(([field, change]) => (
              <FieldDiff
                key={field}
                field={formatFieldName(field)}
                oldValue={change.old}
                newValue={change.new}
              />
            ))}
          </div>
        )}
      </Section>
    );
  };

  const renderEntityDetails = (
    entityKey: string,
    entity: Record<string, any>,
    priorityKeys: string[] = []
  ) => {
    const allKeys = Object.keys(entity);
    const showKeys = [
      ...priorityKeys.filter((k) => k in entity),
      ...allKeys.filter(
        (k) =>
          !priorityKeys.includes(k) &&
          !EXCLUDED_FIELDS.includes(k) &&
          !k.endsWith("Id") &&
          !k.includes("At")
      ),
    ];

    // Keep meaningful values only. Drop null/empty/objects/false-booleans —
    // showing "Request Bills: No" ×15 is visual noise. True booleans get
    // surfaced separately as chips so positive flags remain obvious.
    const meaningful = showKeys
      .map((k) => [k, entity[k]] as const)
      .filter(([, v]) => {
        if (v === null || v === undefined || v === "") return false;
        if (typeof v === "object") return false;
        if (typeof v === "boolean" && v === false) return false;
        return true;
      });

    const detailRows = meaningful.filter(([, v]) => typeof v !== "boolean");
    const trueFlagKeys = meaningful
      .filter(([, v]) => v === true)
      .map(([k]) => k);

    const title =
      entityKey.charAt(0).toUpperCase() + entityKey.slice(1) + " details";

    return (
      <Section>
        <SectionLabel>{title}</SectionLabel>
        <EntityCard>
          {detailRows.length === 0 && trueFlagKeys.length === 0 ? (
            <EmptyNote>No extra details recorded.</EmptyNote>
          ) : (
            <>
              {detailRows.map(([key, value]) => {
                const list = splitListValue(value);
                return (
                  <DefRow key={key}>
                    <DefLabel>{formatFieldName(key)}</DefLabel>
                    <DefValue>
                      {list ? (
                        <ChipRow>
                          {list.map((item, i) => (
                            <Chip key={i} tone="neutral">
                              {item}
                            </Chip>
                          ))}
                        </ChipRow>
                      ) : (
                        formatValue(value)
                      )}
                    </DefValue>
                  </DefRow>
                );
              })}
              {trueFlagKeys.length > 0 && (
                <DefRow>
                  <DefLabel>Included</DefLabel>
                  <DefValue>
                    <ChipRow>
                      {trueFlagKeys.map((k) => (
                        <Chip key={k} tone="new">
                          {icons.check}
                          <span style={{ marginLeft: "0.25rem" }}>
                            {formatFieldName(k).replace(/^Request\s+/i, "")}
                          </span>
                        </Chip>
                      ))}
                    </ChipRow>
                  </DefValue>
                </DefRow>
              )}
            </>
          )}
        </EntityCard>
      </Section>
    );
  };

  const renderCreated = (created: Record<string, any>) => {
    const meaningful = Object.entries(created).filter(([k, v]) => {
      if (EXCLUDED_FIELDS.includes(k)) return false;
      if (v === null || v === undefined || v === "") return false;
      if (typeof v === "boolean" && v === false) return false;
      return true;
    });
    const detailRows = meaningful.filter(([, v]) => typeof v !== "boolean");
    const trueFlagKeys = meaningful
      .filter(([, v]) => v === true)
      .map(([k]) => k);

    return (
      <Section>
        <SectionLabel>What was added</SectionLabel>
        <EntityCard>
          {detailRows.map(([key, value]) => (
            <DefRow key={key}>
              <DefLabel>{formatFieldName(key)}</DefLabel>
              <DefValue>
                {typeof value === "object"
                  ? JSON.stringify(value)
                  : formatValue(value)}
              </DefValue>
            </DefRow>
          ))}
          {trueFlagKeys.length > 0 && (
            <DefRow>
              <DefLabel>Included</DefLabel>
              <DefValue>
                <ChipRow>
                  {trueFlagKeys.map((k) => (
                    <Chip key={k} tone="new">
                      {icons.check}
                      <span style={{ marginLeft: "0.25rem" }}>
                        {formatFieldName(k).replace(/^Request\s+/i, "")}
                      </span>
                    </Chip>
                  ))}
                </ChipRow>
              </DefValue>
            </DefRow>
          )}
        </EntityCard>
      </Section>
    );
  };

  const renderBulkUpdate = () => {
    const count = metadata.count as number | undefined;
    const succeeded = Array.isArray(metadata.succeeded)
      ? (metadata.succeeded as string[])
      : [];
    const failed = Array.isArray(metadata.failed)
      ? (metadata.failed as string[])
      : [];
    const updatedFields = Array.isArray(metadata.updatedFields)
      ? (metadata.updatedFields as string[])
      : [];

    return (
      <>
        <Section>
          <SectionLabel>Summary</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: failed.length > 0 ? "1fr 1fr" : "1fr",
              gap: "0.75rem",
            }}
          >
            <Stat tone="success">
              <div style={{ color: C.addedInk }}>{icons.check}</div>
              <div>
                <StatNumber tone="success">
                  {succeeded.length || count || 0}
                </StatNumber>
                <StatLabel>Succeeded</StatLabel>
              </div>
            </Stat>
            {failed.length > 0 && (
              <Stat tone="danger">
                <div style={{ color: C.removedInk }}>{icons.x}</div>
                <div>
                  <StatNumber tone="danger">{failed.length}</StatNumber>
                  <StatLabel>Failed</StatLabel>
                </div>
              </Stat>
            )}
          </div>
        </Section>

        {updatedFields.length > 0 && (
          <Section>
            <SectionLabel>Fields touched</SectionLabel>
            <ChipRow>
              {updatedFields.map((f, i) => (
                <Chip key={i} tone="neutral">
                  {formatFieldName(f)}
                </Chip>
              ))}
            </ChipRow>
          </Section>
        )}

        {succeeded.length > 0 && (
          <Section>
            <SectionLabel>Items updated ({succeeded.length})</SectionLabel>
            <SoftList>
              {succeeded.map((id) => (
                <ListRow key={id}>
                  <span style={{ color: C.addedInk }}>{icons.check}</span>
                  <RequestReference requestId={id} />
                </ListRow>
              ))}
            </SoftList>
          </Section>
        )}

        {failed.length > 0 && (
          <Section>
            <SectionLabel>Items that failed</SectionLabel>
            <SoftList>
              {failed.map((id) => (
                <ListRow key={id}>
                  <span style={{ color: C.removedInk }}>{icons.x}</span>
                  <RequestReference requestId={id} />
                </ListRow>
              ))}
            </SoftList>
          </Section>
        )}
      </>
    );
  };

  const renderStatusChange = () => (
    <Section>
      <SectionLabel>Status change</SectionLabel>
      <StatusFlow>
        <StatusPill tone="old">
          {icons.x}
          {formatValue(metadata.oldStatus)}
        </StatusPill>
        <span style={{ color: C.subtle }}>{icons.arrow}</span>
        <StatusPill tone="new">
          {icons.check}
          {formatValue(metadata.newStatus)}
        </StatusPill>
      </StatusFlow>
    </Section>
  );

  const renderConnection = () => (
    <Section>
      <SectionLabel>Provider connection</SectionLabel>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
          padding: "1.125rem 1.25rem",
          background: C.card,
          border: `1px solid ${C.hair}`,
          borderRadius: "0.75rem",
        }}
      >
        {metadata.fromId && <ProviderReference providerId={metadata.fromId} />}
        <span style={{ color: C.subtle }}>{icons.arrow}</span>
        {metadata.toId && <ProviderReference providerId={metadata.toId} />}
      </div>
    </Section>
  );

  const renderRequestWithMedicals = () => {
    const req = metadata.request as Record<string, any>;
    const mrs = Array.isArray(metadata.medicalRequests)
      ? metadata.medicalRequests
      : [];
    return (
      <>
        {renderEntityDetails("request", req, [
          "status",
          "requestedBy",
          "requesterEmails",
          "requestNotes",
          "serviceDates",
          "submittedDate",
        ])}
        {mrs.length > 0 && (
          <Section>
            <SectionLabel>Medical requests ({mrs.length})</SectionLabel>
            <SoftList>
              {mrs.map((mr: any, i: number) => (
                <ListRow key={i}>
                  <span style={{ color: C.brand }}>{icons.layers}</span>
                  <strong style={{ fontWeight: 600, color: C.ink }}>
                    {mr.requestType || `Request ${i + 1}`}
                  </strong>
                  {mr.isSent !== undefined && (
                    <Chip tone={mr.isSent ? "new" : "neutral"}>
                      {mr.isSent ? "Sent" : "Not sent"}
                    </Chip>
                  )}
                  {mr.certified !== undefined && (
                    <Chip tone={mr.certified ? "new" : "neutral"}>
                      {mr.certified ? "Certified" : "Not certified"}
                    </Chip>
                  )}
                </ListRow>
              ))}
            </SoftList>
          </Section>
        )}
      </>
    );
  };

  const renderFallback = () => (
    <Section>
      <SectionLabel>Details</SectionLabel>
      <EntityCard>
        {Object.entries(metadata).map(([key, value]) => (
          <DefRow key={key}>
            <DefLabel>{formatFieldName(key)}</DefLabel>
            <DefValue>
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : formatValue(value)}
            </DefValue>
          </DefRow>
        ))}
      </EntityCard>
    </Section>
  );

  /* -------------------- dispatcher ------------------------------- */

  const renderMetadata = () => {
    if (metadata.changes && typeof metadata.changes === "object") {
      return renderChanges(metadata.changes);
    }

    if (metadata.request && metadata.medicalRequests) {
      return renderRequestWithMedicals();
    }

    const entityKeys = [
      "provider",
      "patient",
      "firm",
      "user",
      "request",
      "medicalRequest",
    ];
    const foundKey = entityKeys.find(
      (k) => metadata[k] && typeof metadata[k] === "object"
    );
    if (foundKey) {
      const priorityMap: Record<string, string[]> = {
        provider: [
          "name",
          "type",
          "status",
          "email",
          "phone",
          "address",
          "city",
          "state",
          "zip",
        ],
        patient: [
          "name",
          "dateOfBirth",
          "address",
          "city",
          "state",
          "zip",
          "phone",
          "email",
        ],
        firm: [
          "name",
          "contactName",
          "contactEmail",
          "phone",
          "address",
          "city",
          "state",
          "zip",
        ],
        user: ["name", "email", "roles", "firmId"],
        request: [
          "status",
          "requestedBy",
          "requesterEmails",
          "requestNotes",
          "serviceDates",
          "submittedDate",
        ],
        medicalRequest: ["requestType", "requestNotes", "isSent", "certified"],
      };
      return renderEntityDetails(
        foundKey,
        metadata[foundKey],
        priorityMap[foundKey]
      );
    }

    if (metadata.created && typeof metadata.created === "object") {
      return renderCreated(metadata.created);
    }

    if (
      metadata.count !== undefined ||
      metadata.succeeded ||
      metadata.failed ||
      metadata.updatedFields
    ) {
      return renderBulkUpdate();
    }

    if (metadata.oldStatus !== undefined || metadata.newStatus !== undefined) {
      return renderStatusChange();
    }

    if (metadata.fromId || metadata.toId) {
      return renderConnection();
    }

    return renderFallback();
  };

  /* -------------------- render ---------------------------------- */

  const entryIdShort = id ? `№ ${id.slice(0, 6)}` : null;
  const entityLabel = entityType
    ? entityType.toLowerCase().replace(/_/g, " ")
    : null;

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title=""
      titleClassName="!hidden"
      onConfirm={onClose}
      confirmText="Done"
      noConfirm={true}
      noCancel={true}
      buttonAlignment="right"
      panelClassName="!max-w-[640px] !p-0 !overflow-hidden"
    >
      <Root>
        <Hero>
          <HeroTopRow>
            <KickerRow>
              <EventDot tone={heroIcon.tone} />
              {entityLabel && <span>{entityLabel}</span>}
              {entityLabel && (eventType || narrative.eventKind) && (
                <KickerSep>·</KickerSep>
              )}
              <span>
                {(eventType || narrative.eventKind).replace(/_/g, " ")}
              </span>
              {createdAt && (
                <>
                  <KickerSep>·</KickerSep>
                  <span title={absoluteTime(createdAt)}>
                    {relativeTime(createdAt)}
                  </span>
                </>
              )}
            </KickerRow>
            {entryIdShort && <EntryId>{entryIdShort}</EntryId>}
          </HeroTopRow>

          <BigTitle>{headline}</BigTitle>

          {narrative.actor && (
            <ActorRow>
              <Initial>{initialsFor(narrative.actor)}</Initial>
              <ActorName>{narrative.actor}</ActorName>
              {createdAt && (
                <>
                  <KickerSep>·</KickerSep>
                  <ActorMeta>{absoluteTime(createdAt)}</ActorMeta>
                </>
              )}
            </ActorRow>
          )}
        </Hero>

        <Body>
          <Section>
            <SectionLabel>Context</SectionLabel>
            <Sentence>
              {narrative.actor ? (
                narrative.eventKind === "attachmentAdd" ||
                narrative.eventKind === "attachmentRemove" ? (
                  <>
                    <strong>{narrative.actor}</strong>{" "}
                    {narrative.eventKind === "attachmentAdd"
                      ? "attached a file to"
                      : "removed an attachment from"}{" "}
                    {narrative.linkedRequestId
                      ? "a note on this request."
                      : narrative.linkedProviderId
                      ? "a note on this provider."
                      : "a note."}
                  </>
                ) : (
                  <>
                    <strong>{narrative.actor}</strong>{" "}
                    {narrative.verb === "changed"
                      ? "changed the status of"
                      : narrative.verb}{" "}
                    {narrative.linkedRequestId
                      ? "this request."
                      : narrative.linkedProviderId
                      ? "this provider."
                      : entityLabel
                      ? `this ${entityLabel}.`
                      : "an item in the system."}
                  </>
                )
              ) : (
                message
              )}
            </Sentence>
            {narrative.linkedRequestId && (
              <div style={{ marginTop: "0.25rem" }}>
                <RequestReference requestId={narrative.linkedRequestId} />
              </div>
            )}
            {narrative.linkedProviderId && (
              <div style={{ marginTop: "0.25rem" }}>
                <ProviderReference providerId={narrative.linkedProviderId} />
              </div>
            )}
          </Section>

          {renderMetadata()}
        </Body>

        <Footer>
          <PrimaryButton onClick={onClose}>Done</PrimaryButton>
        </Footer>
      </Root>
    </Modal>
  );
};
